function _objectDestructuringEmpty(obj) { if (obj == null) throw new TypeError("Cannot destructure " + obj); }
import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import { Search as SearchSemantic } from 'semantic-ui-react';
import SearchCategory from "./SearchCategory";
import SearchResult from "./SearchResult";
import SearchResults from "./SearchResults";
import * as SUI from "../../utils/SUI";

/**
 * A search module allows a user to query for results from a selection of data
 */
import { jsx as _jsx } from "react/jsx-runtime";
var Search = function Search(props) {
  var rest = Object.assign({}, (_objectDestructuringEmpty(props), props));
  return /*#__PURE__*/_jsx(SearchSemantic, Object.assign({}, rest));
};
Search.handledProps = ["aligned", "as", "category", "categoryRenderer", "className", "defaultOpen", "defaultValue", "fluid", "icon", "input", "loading", "minCharacters", "noResultsDescription", "noResultsMessage", "onBlur", "onFocus", "onMouseDown", "onResultSelect", "onSearchChange", "onSelectionChange", "open", "resultRenderer", "results", "selectFirstResult", "showNoResults", "size", "value"];
Search.propTypes = {
  /** An element type to render as (string or function). */
  as: PropTypes.oneOfType([PropTypes.func, PropTypes.string, PropTypes.symbol]),
  // ------------------------------------
  // Behavior
  // ------------------------------------

  /** Initial value of open. */
  defaultOpen: PropTypes.bool,
  /** Initial value. */
  defaultValue: PropTypes.string,
  /** Shorthand for Icon. */
  icon: PropTypes.oneOfType([PropTypes.node, PropTypes.object]),
  /** Minimum characters to query for results */
  minCharacters: PropTypes.number,
  /** Additional text for "No Results" message with less emphasis. */
  noResultsDescription: PropTypes.node,
  /** Message to display when there are no results. */
  noResultsMessage: PropTypes.node,
  /** Controls whether or not the results menu is displayed. */
  open: PropTypes.bool,
  /* eslint-disable array-element-newline */
  /**
     * One of:
     * - array of Search.Result props e.g. `{ title: '', description: '' }` or
     * - object of categories e.g. `{ name: '', results: [{ title: '', description: '' }]`
     */
  results: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.shape(SearchResult.propTypes)), PropTypes.shape(SearchCategory.propTypes)]),
  /* eslint-enable array-element-newline */

  /** Whether the search should automatically select the first result after searching. */
  selectFirstResult: PropTypes.bool,
  /** Whether a "no results" message should be shown if no results are found. */
  showNoResults: PropTypes.bool,
  /** Current value of the search input. Creates a controlled component. */
  value: PropTypes.string,
  // ------------------------------------
  // Rendering
  // ------------------------------------

  /**
     * Renders the SearchCategory contents.
     *
     * @param {object} props - The SearchCategory props object.
     * @returns {*} - Renderable SearchCategory contents.
     */
  categoryRenderer: PropTypes.func,
  /**
     * Renders the SearchResult contents.
     *
     * @param {object} props - The SearchResult props object.
     * @returns {*} - Renderable SearchResult contents.
     */
  resultRenderer: PropTypes.func,
  // ------------------------------------
  // Callbacks
  // ------------------------------------

  /**
     * Called on blur.
     *
     * @param {SyntheticEvent} event - React's original SyntheticEvent.
     * @param {object} data - All props.
     */
  onBlur: PropTypes.func,
  /**
     * Called on focus.
     *
     * @param {SyntheticEvent} event - React's original SyntheticEvent.
     * @param {object} data - All props.
     */
  onFocus: PropTypes.func,
  /**
     * Called on mousedown.
     *
     * @param {SyntheticEvent} event - React's original SyntheticEvent.
     * @param {object} data - All props.
     */
  onMouseDown: PropTypes.func,
  /**
     * Called when a result is selected.
     *
     * @param {SyntheticEvent} event - React's original SyntheticEvent.
     * @param {object} data - All props.
     */
  onResultSelect: PropTypes.func,
  /**
     * Called on search input change.
     *
     * @param {SyntheticEvent} event - React's original SyntheticEvent.
     * @param {object} data - All props, includes current value of search input.
     */
  onSearchChange: PropTypes.func,
  /**
     * Called when the active selection index is changed.
     *
     * @param {SyntheticEvent} event - React's original SyntheticEvent.
     * @param {object} data - All props.
     */
  onSelectionChange: PropTypes.func,
  // ------------------------------------
  // Style
  // ------------------------------------

  /** A search can have its results aligned to its left or right container edge. */
  aligned: PropTypes.string,
  /** A search can display results from remote content ordered by categories. */
  category: PropTypes.bool,
  /** Additional classes. */
  className: PropTypes.string,
  /** A search can have its results take up the width of its container. */
  fluid: PropTypes.bool,
  /** A search input can take up the width of its container. */
  input: PropTypes.string,
  /** A search can show a loading indicator. */
  loading: PropTypes.bool,
  /** A search can have different sizes. */
  size: PropTypes.oneOf(_.without(SUI.SIZES, 'medium'))
};
Search.defaultProps = {
  icon: 'search',
  input: 'text',
  minCharacters: 1,
  noResultsMessage: 'Aucun résultat trouvé',
  showNoResults: true
};
Search.Category = SearchCategory;
Search.Result = SearchResult;
Search.Results = SearchResults;
export default Search;